import {
  kvGet, kvSet, type LineupAssignmentRow, type LineupLineRow, type LineupSectionRow, type LineupSpotRow, type RouteRow,
  type ScheduleAssignmentRow, type ScheduleDayRow, type TimeoffRequestRow, type TruckRow,
} from '@routeback/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { useApp } from '../../lib/app';
import { supabase } from '../../lib/supabase';
import { addDays } from '../timeoff/timeoff';
import type { RosterEntry } from './schedule';

/** Live company rows of one synced table, without trashed ones. */
function useLive<T extends { company_id: string; deleted_at: string | null }>(table: string, filter?: (r: T) => boolean): T[] {
  const app = useApp();
  return useLiveQuery(
    async () => (await app.db.table<T, string>(table).where('company_id').equals(app.company.id).toArray()).filter((r) => !r.deleted_at && (!filter || filter(r))),
    [app.db, app.company.id, table],
    [] as T[],
  );
}

/** Everything the schedule screen needs for one week, read from the phone (works offline). */
export function useWeekData(from: string, to: string) {
  const app = useApp();
  const routes = useLive<RouteRow>('routes', (r) => r.active);
  const assignments = useLiveQuery(
    async () => (await app.db.table<ScheduleAssignmentRow, string>('schedule_assignments').where('work_date').between(from, to, true, true).toArray())
      .filter((a) => a.company_id === app.company.id && !a.deleted_at),
    [app.db, app.company.id, from, to],
    [] as ScheduleAssignmentRow[],
  );
  const days = useLive<ScheduleDayRow>('schedule_days').filter((d) => d.work_date >= from && d.work_date <= to);
  const trucks = useLive<TruckRow>('trucks');
  return { routes, assignments, days, trucks };
}

/**
 * Names on the team schedule and who's free each day. Drivers can't read
 * other people's profiles, so in the cloud this asks `schedule_roster` and
 * keeps the answer for no-signal use. In local mode the only person is you.
 */
export function useRoster(from: string, to: string): { roster: RosterEntry[]; loaded: boolean } {
  const app = useApp();
  const key = `roster:${app.company.id}:${from}:${to}`;
  const [state, setState] = useState<{ key: string; roster: RosterEntry[]; loaded: boolean }>({ key, roster: [], loaded: false });
  const lastSync = app.syncStatus.lastSyncAt;
  const myOff = useLive<TimeoffRequestRow>('timeoff_requests', (r) => r.user_id === app.userId && r.status === 'approved' && !!r.start_date);

  useEffect(() => {
    if (!supabase) return;
    let alive = true;
    void (async () => {
      const cached = await kvGet<RosterEntry[]>(key);
      if (alive && cached) setState({ key, roster: cached, loaded: true });
      const { data, error } = await supabase.rpc('schedule_roster', { c: app.company.id, from_date: from, to_date: to });
      if (!alive) return;
      if (error) {
        setState((s) => (s.key === key ? { ...s, loaded: true } : { key, roster: cached ?? [], loaded: true }));
        return;
      }
      const rows = ((data ?? []) as RosterEntry[]).map((r) => ({ ...r, off_dates: r.off_dates ?? [] }));
      setState({ key, roster: rows, loaded: true });
      await kvSet(key, rows);
    })();
    return () => { alive = false; };
  }, [key, from, to, app.company.id, lastSync]);

  if (!supabase) {
    const off: string[] = [];
    for (const r of myOff) for (let d = r.start_date!; d <= r.end_date! && d <= to; d = addDays(d, 1)) if (d >= from) off.push(d);
    return { roster: [{ user_id: app.userId, display_name: app.userName || 'You', on_schedule: true, off_dates: off }], loaded: true };
  }
  return state.key === key ? { roster: state.roster, loaded: state.loaded } : { roster: [], loaded: false };
}

export interface LineupChange { truck_id: string | null; from_spot_id: string | null; to_spot_id: string | null; changed_at: string }

/** The lineup board from the phone, plus moves in the last 24 h (online; cached). */
export function useLineup() {
  const app = useApp();
  const lines = useLive<LineupLineRow>('lineup_lines');
  const sections = useLive<LineupSectionRow>('lineup_sections');
  const spots = useLive<LineupSpotRow>('lineup_spots');
  const placed = useLive<LineupAssignmentRow>('lineup_assignments');
  const trucks = useLive<TruckRow>('trucks');
  const key = `lineup-changes:${app.company.id}`;
  const [changes, setChanges] = useState<LineupChange[]>([]);
  const lastSync = app.syncStatus.lastSyncAt;
  useEffect(() => {
    if (!supabase) return;
    let alive = true;
    void (async () => {
      const cached = await kvGet<LineupChange[]>(key);
      if (alive && cached) setChanges(cached);
      const since = new Date(Date.now() - 24 * 3600_000).toISOString();
      const { data, error } = await supabase.from('lineup_changes').select('truck_id,from_spot_id,to_spot_id,changed_at')
        .eq('company_id', app.company.id).gt('changed_at', since).order('changed_at');
      if (!alive || error) return;
      setChanges((data ?? []) as LineupChange[]);
      await kvSet(key, data ?? []);
    })();
    return () => { alive = false; };
  }, [key, app.company.id, lastSync]);
  const dayAgo = new Date(Date.now() - 24 * 3600_000).toISOString();
  return { lines, sections, spots, placed, trucks, changes: changes.filter((c) => c.changed_at > dayAgo) };
}
